/**
 * Density mode — design/09 §6, re-stepped by brief part 1 §1 (DEC-95).
 *
 *   "Three row heights — Comfortable 40px, Default 32px, Compact 24px — user-selectable
 *    and PERSISTED PER USER. Compact is not a degraded mode; experienced planners work
 *    in it all day, and it is the mode screenshots are reviewed in."
 *
 * The brief moves the three heights to Comfortable 48 · Normal 40 · Compact 32 and gives
 * each a text size, 14 / 14 / 13 px; a totals row is 44px in every density. The values
 * live in `tokens/layout.json`; this file only reads them.
 *
 * The persistence is the part usually skipped. A planner who sets Compact once and finds
 * it reset on the next login learns that their preferences are not respected, and
 * G2.3's "saved views per user" requirement starts from the same premise.
 */

import type { CSSProperties } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DENSITY_MODES, densityRowHeight, densityTextSize, type DensityMode } from '../tokens/generated/tokens';

export const DENSITY_STORAGE_KEY = 'bata.density';

interface DensityState {
  readonly mode: DensityMode;
  readonly rowHeight: number;
  setMode: (mode: DensityMode) => void;
}

export const useDensityStore = create<DensityState>()(
  persist(
    (set) => ({
      // `default` (32px) is the default; Compact is a choice, not a fallback.
      mode: 'default',
      rowHeight: densityRowHeight.default,
      setMode: (mode) => set({ mode, rowHeight: densityRowHeight[mode] }),
    }),
    {
      name: DENSITY_STORAGE_KEY,
      partialize: (state) => ({ mode: state.mode }),
      // The row height is DERIVED from the mode, so it is recomputed on rehydrate through
      // setMode rather than assigned — the state is readonly by design, and a store whose
      // two fields can disagree is a store that will disagree.
      onRehydrateStorage: () => (state) => {
        if (state) state.setMode(state.mode);
      },
    },
  ),
);

export function useDensity(): { mode: DensityMode; rowHeight: number; setMode: (m: DensityMode) => void; modes: typeof DENSITY_MODES } {
  const mode = useDensityStore((s) => s.mode);
  const setMode = useDensityStore((s) => s.setMode);
  return { mode, rowHeight: densityRowHeight[mode], setMode, modes: DENSITY_MODES };
}

/**
 * A row's inline style in a density: its height and the text size the brief gives it (§1).
 *
 * The size is set on the ROW and reaches the cells through the type-scale custom
 * properties: a cell that says `text-body` or `text-numeric` reads
 * `--bata-type-body-size` / `--bata-type-numeric-size`, and those are overridden here, so
 * every cell of the row follows the density without each one being told. `fontSize` covers
 * a cell that states no scale and inherits from the table.
 *
 * Pass `height` to override the row's height — the totals row's fixed 44px.
 */
export function densityRowStyle(mode: DensityMode, height: number = densityRowHeight[mode]): CSSProperties {
  const size = `${densityTextSize[mode]}px`;
  return {
    height,
    fontSize: size,
    '--bata-type-body-size': size,
    '--bata-type-numeric-size': size,
  } as CSSProperties;
}
